import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Award, Send, CheckCircle,
  Eye, MessageSquare, RefreshCw, DollarSign, Activity,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  LayoutTemplate, Clock, Zap, Bell, Filter,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
} from 'recharts';
import axios from '../../../../config/axiosConfig';
import transactionalApi from '../../../../api/transactional.api';
import { useFeatureFlag, FEATURE_KEYS } from '../../../../utils/planFeatures';
import './AnalyticsPage.css';
import { Skeleton as BoneSkeleton } from 'boneyard-js/react';
import CampaignAnalyticsModal from './CampaignAnalyticsModal';
import MessageAnalyticsModal from './MessageAnalyticsModal';
import CostAnalyticsModal from './CostAnalyticsModal';
import TemplateAnalyticsModal from './TemplateAnalyticsModal';

/* ── Palette ── */
const COLORS = {
  primary: '#25D366', secondary: '#128C7E', info: '#3b82f6',
  success: '#10b981', warning: '#f59e0b', danger: '#ef4444',
  purple: '#8b5cf6', pink: '#ec4899', teal: '#075E54',
  indigo: '#6366f1',
};
const PIE_COLORS = ['#25D366', '#128C7E', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];

/* ── Tiny pagination hook ── */
const usePagination = (data, perPage = 5) => {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [data]);
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / perPage));
  const safeP = Math.min(page, totalPages);
  const sliced = (data || []).slice((safeP - 1) * perPage, safeP * perPage);
  return { page: safeP, totalPages, sliced, setPage, total: data?.length || 0 };
};

/* ── Pagination bar component ── */
const PaginationBar = ({ page, totalPages, setPage, total, label = 'items' }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="wa-pagination">
      <span className="wa-pagination-info">{total} {label}</span>
      <div className="wa-pagination-controls">
        <button className="wa-pg-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={14} /></button>
        <span className="wa-pg-text">{page} / {totalPages}</span>
        <button className="wa-pg-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={14} /></button>
      </div>
    </div>
  );
};

/* ── Custom recharts tooltip ── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="wa-chart-tooltip">
      {label && <p className="wa-tooltip-label">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="wa-tooltip-row">
          <span className="wa-tooltip-dot" style={{ background: p.color }} />
          {p.name}: <strong>{(p.value ?? 0).toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

/* ── Collapsible section ── */
const Collapsible = ({ title, count, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="wa-collapsible">
      <button className="wa-collapsible-toggle" onClick={() => setOpen(!open)}>
        <span>{title}{count != null && <span className="wa-badge">{count}</span>}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="wa-collapsible-body">{children}</div>}
    </div>
  );
};

/* ── Skeleton helper ── */
const Skeleton = ({ rows = 3, style }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16, ...style }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="wa-skl" style={{ height: 18, borderRadius: 6, animationDelay: `${i * 80}ms` }} />
    ))}
  </div>
);

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
const AnalyticsPage = () => {
  const { enabled: txApiEnabled } = useFeatureFlag(FEATURE_KEYS.TRANSACTIONAL_API);
  const { enabled: byoEnabled } = useFeatureFlag(FEATURE_KEYS.BYO_WEBHOOK);

  const [templatePerformance, setTemplatePerformance] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [periodAnalytics, setPeriodAnalytics] = useState([]);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Phase 3: source filter (all | addressbook | byo_webhook | transactional)
  const [sourceFilter, setSourceFilter] = useState('all');
  // Phase 3: transactional sends KPI
  const [txTotal, setTxTotal] = useState(null);

  // Campaign analytics modal
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignAnalyticsData, setCampaignAnalyticsData] = useState(null);
  const [campaignAnalyticsLoading, setCampaignAnalyticsLoading] = useState(false);

  // Message analytics modal
  const [msgModalOpen, setMsgModalOpen] = useState(false);
  const [msgAnalyticsData, setMsgAnalyticsData] = useState(null);
  const [msgAnalyticsLoading, setMsgAnalyticsLoading] = useState(false);

  // Cost analytics modal
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [costAnalyticsData, setCostAnalyticsData] = useState(null);
  const [costAnalyticsLoading, setCostAnalyticsLoading] = useState(false);

  // Template analytics modal
  const [tplModalOpen, setTplModalOpen] = useState(false);
  const [tplAnalyticsData, setTplAnalyticsData] = useState(null);
  const [tplAnalyticsLoading, setTplAnalyticsLoading] = useState(false);

  const [insights, setInsights] = useState({
    topPerformingTemplate: { name: '-', responseRate: 0 },
    totalCampaigns: 0,
    totalMessages: 0,
    totalDelivered: 0,
    totalSeen: 0,
    totalResponded: 0,
    totalTemplates: 0,
    totalCost: 0,
    deliveryRate: 0,
    weekGrowth: 0,
    monthGrowth: 0,
  });

  const fetchAll = useCallback(async () => {
    try {
      const [tplRes, periodRes, campaignRes, msgRes, costRes, dashTplRes] = await Promise.allSettled([
        axios.get('/campaign/analytics/templates'),
        axios.post('/campaign/analytics/periods'),
        axios.get('/dashboard/campaign-analytics'),
        axios.get('/dashboard/analytics/messages'),
        axios.get('/dashboard/analytics/costs'),
        axios.get('/dashboard/analytics/templates'),
      ]);

      /* ── Campaign analytics (total campaigns) ── */
      if (campaignRes.status === 'fulfilled') {
        const rd = campaignRes.value?.data;
        if (rd?.status === true && rd?.entity) {
          setCampaignAnalyticsData(rd.entity);
          setInsights(prev => ({
            ...prev,
            totalCampaigns: rd.entity.campaigns?.total || 0,
          }));
        }
      }

      /* ── Message analytics (total messages, delivery rate, seen) ── */
      if (msgRes.status === 'fulfilled') {
        const rd = msgRes.value?.data;
        if (rd?.status === true && rd?.entity) {
          setMsgAnalyticsData(rd.entity);
          const funnel = rd.entity.deliveryFunnel || {};
          const totalMsgs = funnel.totalMessages || 0;
          const totalDel  = funnel.totalDelivered || 0;
          const deliveryRate = funnel.deliveryRate != null
            ? parseFloat(funnel.deliveryRate)
            : totalMsgs > 0 ? parseFloat(((totalDel / totalMsgs) * 100).toFixed(1)) : 0;
          setInsights(prev => ({
            ...prev,
            totalMessages:  totalMsgs,
            totalDelivered: totalDel,
            totalSeen:      funnel.totalSeen || funnel.seen || 0,
            deliveryRate,
          }));
        }
      }

      /* ── Cost analytics (total cost KPI) ── */
      if (costRes.status === 'fulfilled') {
        const rd = costRes.value?.data;
        if (rd?.status === true && rd?.entity) {
          setCostAnalyticsData(rd.entity);
          setInsights(prev => ({
            ...prev,
            totalCost: parseFloat(rd.entity.totalCost?.netCost || 0),
          }));
        }
      }

      /* ── Dashboard template analytics ── */
      if (dashTplRes.status === 'fulfilled') {
        const rd = dashTplRes.value?.data;
        if (rd?.status === true && rd?.entity) {
          setTplAnalyticsData(rd.entity);
          setInsights(prev => ({
            ...prev,
            totalTemplates: rd.entity.inventory?.total || 0,
          }));
        }
      }

      /* ── Template analytics ── */
      if (tplRes.status === 'fulfilled') {
        const rd = tplRes.value?.data;
        if (rd && (rd.statuscode === 200 || rd.status === true)) {
          const templates = rd.entity?.templates || [];
          setTemplatePerformance(templates);
          if (templates.length > 0) {
            const topTpl = templates.reduce((prev, cur) =>
              parseFloat(prev.response_rate) > parseFloat(cur.response_rate) ? prev : cur
            );
            setInsights(prev => ({
              ...prev,
              topPerformingTemplate: { name: topTpl.templatename, responseRate: parseFloat(topTpl.response_rate) || 0 },
            }));
          }
        }
      }

      /* ── Period analytics ── */
      if (periodRes.status === 'fulfilled') {
        const rd = periodRes.value?.data;
        if (rd && (rd.statuscode === 200 || rd.status === true)) {
          const analytics = rd.entity?.analytics || rd.data?.analytics || [];
          const periodLabels = {
            today: 'Today', yesterday: 'Yesterday',
            thisweek: 'This Week', lastweek: 'Last Week',
            thismonth: 'This Month', lastmonth: 'Last Month',
          };
          const formatted = analytics.map(item => ({
            ...item,
            period: periodLabels[item.period] || item.period,
            periodKey: item.period,
            sent: parseInt(item.sent) || 0,
            delivered: parseInt(item.delivered) || 0,
            opened: parseInt(item.opened) || 0,
            responded: parseInt(item.responded) || 0,
            cost: parseFloat(item.cost) || 0,
          }));
          setPeriodAnalytics(formatted);

          const tw = formatted.find(p => p.periodKey === 'thisweek');
          const lw = formatted.find(p => p.periodKey === 'lastweek');
          const tm = formatted.find(p => p.periodKey === 'thismonth');
          const lm = formatted.find(p => p.periodKey === 'lastmonth');
          setInsights(prev => ({
            ...prev,
            weekGrowth: tw && lw && lw.sent > 0 ? parseFloat(((tw.sent - lw.sent) / lw.sent * 100).toFixed(1)) : 0,
            monthGrowth: tm && lm && lm.sent > 0 ? parseFloat(((tm.sent - lm.sent) / lm.sent * 100).toFixed(1)) : 0,
            totalResponded: formatted.reduce((s, p) => s + p.responded, 0),
          }));
        }
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  const handleRefresh = () => { setRefreshing(true); setLoading(true); fetchAll(); };

  const openMsgModal = async () => {
    setMsgModalOpen(true);
    if (msgAnalyticsData) return; // already loaded
    setMsgAnalyticsLoading(true);
    try {
      const res = await axios.get('/dashboard/analytics/messages');
      const rd = res.data;
      if (rd?.status === true && rd?.entity) {
        setMsgAnalyticsData(rd.entity);
      }
    } catch (err) {
      console.error('Message analytics fetch error:', err);
    } finally {
      setMsgAnalyticsLoading(false);
    }
  };

  // Phase 3 — Transactional sends count (last 30 days)
  useEffect(() => {
    if (!txApiEnabled) { setTxTotal(0); return; }
    let active = true;
    (async () => {
      try {
        const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
        const res = await transactionalApi.list({ from: since }, 1, 1);
        const data = res.data?.entity ?? res.data?.data ?? {};
        if (active) setTxTotal(data.total ?? (data.items?.length || 0));
      } catch (err) {
        if (active) setTxTotal(0);
      }
    })();
    return () => { active = false; };
  }, [txApiEnabled, refreshing]);

  /* ── Formatters ── */
  const fmtNum = (n) => (n ?? 0).toLocaleString();
  const fmtPct = (n) => `${(parseFloat(n) || 0).toFixed(1)}%`;

  /* ── Derived chart data ── */
  const donutData = useMemo(() => {
    const s = insights;
    return [
      { name: 'Delivered', value: s.totalDelivered },
      { name: 'Seen', value: s.totalSeen },
      { name: 'Responded', value: s.totalResponded || 0 },
      { name: 'Undelivered', value: Math.max(0, s.totalMessages - s.totalDelivered) },
    ].filter(d => d.value > 0);
  }, [insights]);

  const periodBarData = useMemo(() => {
    return periodAnalytics.map(p => ({
      name: p.period,
      Sent: p.sent,
      Delivered: p.delivered,
      Opened: p.opened,
      Responded: p.responded,
    }));
  }, [periodAnalytics]);

  const periodAreaData = useMemo(() => {
    return periodAnalytics.map(p => ({
      name: p.period,
      Sent: p.sent,
      Delivered: p.delivered,
      Opened: p.opened,
    }));
  }, [periodAnalytics]);

  const templateBarData = useMemo(() => {
    return templatePerformance.slice(0, 8).map(t => ({
      name: (t.templatename || '').replace(/_/g, ' ').length > 16
        ? t.templatename.replace(/_/g, ' ').substring(0, 16) + '…'
        : (t.templatename || '').replace(/_/g, ' '),
      Messages: parseInt(t.no_of_msg || 0),
      Delivered: parseInt(t.no_of_msg_deliverd || 0),
      Seen: parseInt(t.no_of_msg_seen || 0),
    }));
  }, [templatePerformance]);

  const costByPeriod = useMemo(() => {
    return periodAnalytics.map(p => ({
      name: p.period,
      Cost: p.cost,
    }));
  }, [periodAnalytics]);

  /* ── Paginated data ── */
  const templatePg = usePagination(templatePerformance, 5);

  /* ═══════════ RENDER ═══════════ */
  return (
    <>
    <div className="wa-page">
      {/* ── Header ── */}
      <div className="wa-header">
        <div>
          <h1 className="wa-title">
            <Activity size={22} className="wa-title-icon" />
            WhatsApp Analytics
          </h1>
          <p className="wa-subtitle">Track your messaging performance and template insights</p>
        </div>
        <div className="wa-header-actions">
          <button
            className={`wa-refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh analytics"
            aria-label="Refresh analytics"
          >
            <RefreshCw size={15} className={refreshing ? 'spin-icon' : ''} />
          </button>
        </div>
      </div>

      {/* ── Source filter dropdown (Phase 3) ── */}
      <div className="wa-source-bar">
        <div className="wa-source-filter-label">
          <Filter size={13} />
          <span>Source:</span>
        </div>
        <div className="wa-source-select-wrap">
          <select
            className="wa-source-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            <option value="addressbook">Address Book</option>
            {byoEnabled && <option value="byo_webhook">BYO Webhook</option>}
            {txApiEnabled && <option value="transactional">Transactional</option>}
          </select>
          <ChevronDown size={13} className="wa-source-chevron" />
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      <div className="wa-tabs">
        {[
          { key: 'overview', label: 'Overview', icon: <BarChart3 size={15} /> },
          { key: 'periods', label: 'Period Analytics', icon: <Clock size={15} /> },
          { key: 'templates', label: 'Templates', icon: <LayoutTemplate size={15} /> },
        ].map(t => (
          <button key={t.key} className={`wa-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW TAB ═══ */}
      {activeTab === 'overview' && (
        <>
          {/* KPI Row — Unified compact strip */}
          <BoneSkeleton
            name="analytics-kpi"
            loading={loading}
            fallback={
              <div className="wa-kpi-row">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="wa-kpi-card">
                    <div className="wa-skl" style={{ width: 36, height: 36, borderRadius: 10 }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div className="wa-skl" style={{ width: '60%', height: 20 }} />
                      <div className="wa-skl" style={{ width: '40%', height: 12 }} />
                    </div>
                  </div>
                ))}
              </div>
            }
          >
            <div className="wa-kpi-row wa-kpi-row-unified">
              {[
                { label: 'Total Campaigns', value: fmtNum(insights.totalCampaigns), icon: <Send size={17} />, color: 'primary', onClick: () => setCampaignModalOpen(true) },
                { label: 'Total Messaging', value: fmtNum(insights.totalMessages), icon: <MessageSquare size={17} />, color: 'success', onClick: () => openMsgModal() },
                { label: 'Delivery Rate', value: fmtPct(insights.deliveryRate), icon: <Zap size={17} />, color: 'info' },
                { label: 'Seen', value: fmtNum(insights.totalSeen), icon: <Eye size={17} />, color: 'purple' },
                { label: 'Templates', value: fmtNum(insights.totalTemplates || 0), icon: <LayoutTemplate size={17} />, color: 'info', onClick: () => setTplModalOpen(true) },
                { label: 'Total Cost', value: `₹${insights.totalCost.toFixed(2)}`, icon: <DollarSign size={17} />, color: 'warning', onClick: () => setCostModalOpen(true) },
                { label: 'Weekly', value: `${insights.weekGrowth >= 0 ? '+' : ''}${insights.weekGrowth}%`, icon: insights.weekGrowth >= 0 ? <TrendingUp size={17} /> : <TrendingDown size={17} />, color: insights.weekGrowth >= 0 ? 'success' : 'danger' },
                { label: 'Monthly', value: `${insights.monthGrowth >= 0 ? '+' : ''}${insights.monthGrowth}%`, icon: insights.monthGrowth >= 0 ? <TrendingUp size={17} /> : <TrendingDown size={17} />, color: insights.monthGrowth >= 0 ? 'success' : 'danger' },
              ].map((k, i) => (
                <div
                  key={i}
                  className="wa-kpi-card"
                  onClick={k.onClick || undefined}
                  style={k.onClick ? { cursor: 'pointer' } : undefined}
                  title={k.onClick ? 'Click to view details' : undefined}
                >
                  <div className={`wa-kpi-icon wa-kpi-icon-${k.color}`}>{k.icon}</div>
                  <div>
                    <h3 className="wa-kpi-value">{k.value}</h3>
                    <p className="wa-kpi-label">{k.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </BoneSkeleton>

          {/* Charts Row */}
          <div className="wa-grid-2">
            {/* Donut – Message Breakdown */}
            <div className="wa-card">
              <div className="wa-card-head">
                <h3 className="wa-card-title"><MessageSquare size={16} /> Message Breakdown</h3>
                {!loading && donutData.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {donutData.map((d, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.67rem', fontWeight: 600, color: '#475569', background: '#f8fafc', border: '1px solid #e8edf2', borderRadius: 6, padding: '2px 7px', lineHeight: 1.5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                        {d.name}: {fmtNum(d.value)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="wa-card-body" style={{ minHeight: 260 }}>
                <BoneSkeleton name="analytics-donut" loading={loading} fallback={<Skeleton rows={5} />}>
                  {donutData.length === 0 ? (
                    <div className="wa-empty"><BarChart3 size={28} /><p>No message data yet</p></div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" stroke="none">
                          {donutData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} />
                        <Legend verticalAlign="bottom" iconType="circle" iconSize={8}
                          formatter={(v) => <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{v}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </BoneSkeleton>
              </div>
            </div>

            {/* Bar – Period Performance */}
            <div className="wa-card">
              <div className="wa-card-head">
                <h3 className="wa-card-title"><Clock size={16} /> Period Performance</h3>
                {!loading && periodBarData.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {[{ label: 'Sent', color: COLORS.primary, value: insights.totalMessages }, { label: 'Delivered', color: COLORS.info, value: insights.totalDelivered }].map((c, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.67rem', fontWeight: 600, color: '#475569', background: '#f8fafc', border: '1px solid #e8edf2', borderRadius: 6, padding: '2px 7px', lineHeight: 1.5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                        {c.label}: {fmtNum(c.value)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="wa-card-body" style={{ minHeight: 260 }}>
                <BoneSkeleton name="analytics-period-bar" loading={loading} fallback={<Skeleton rows={5} />}>
                  {periodBarData.length === 0 ? (
                    <div className="wa-empty"><BarChart3 size={28} /><p>No period data yet</p></div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={periodBarData} barGap={2} barSize={14}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="Sent" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Delivered" fill={COLORS.info} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </BoneSkeleton>
              </div>
            </div>
          </div>

          {/* Area Chart – Trend */}
          {periodAreaData.length > 0 && !loading && (
            <div className="wa-card wa-card-full">
              <div className="wa-card-head">
                <h3 className="wa-card-title"><Activity size={16} /> Messaging Trend</h3>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {[{ label: 'Sent', color: COLORS.primary, value: insights.totalMessages }, { label: 'Delivered', color: COLORS.success, value: insights.totalDelivered }, { label: 'Opened', color: COLORS.info, value: insights.totalSeen }].map((c, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.67rem', fontWeight: 600, color: '#475569', background: '#f8fafc', border: '1px solid #e8edf2', borderRadius: 6, padding: '2px 7px', lineHeight: 1.5 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                      {c.label}: {fmtNum(c.value)}
                    </span>
                  ))}
                </div>
              </div>
              <div className="wa-card-body">
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={periodAreaData}>
                    <defs>
                      <linearGradient id="waGradSent" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.15} /><stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} /></linearGradient>
                      <linearGradient id="waGradDel" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.success} stopOpacity={0.15} /><stop offset="100%" stopColor={COLORS.success} stopOpacity={0} /></linearGradient>
                      <linearGradient id="waGradOpen" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.info} stopOpacity={0.15} /><stop offset="100%" stopColor={COLORS.info} stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="Sent" stroke={COLORS.primary} fill="url(#waGradSent)" strokeWidth={2} />
                    <Area type="monotone" dataKey="Delivered" stroke={COLORS.success} fill="url(#waGradDel)" strokeWidth={2} />
                    <Area type="monotone" dataKey="Opened" stroke={COLORS.info} fill="url(#waGradOpen)" strokeWidth={2} />
                    <Legend verticalAlign="top" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingBottom: 8 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ PERIOD ANALYTICS TAB ═══ */}
      {activeTab === 'periods' && (
        <>
          {/* Period Comparison + Cost side by side */}
          {(periodBarData.length > 0 || costByPeriod.length > 0) && !loading && (
            <div className="wa-grid-2" style={{ marginBottom: 16 }}>

              {/* Period Comparison */}
              {periodBarData.length > 0 ? (
                <div className="wa-card" style={{ marginBottom: 0 }}>
                  <div className="wa-card-head">
                    <h3 className="wa-card-title"><BarChart3 size={16} /> Period Comparison</h3>
                  </div>
                  <div className="wa-card-body">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={periodBarData} barSize={18}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={32} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend verticalAlign="top" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.72rem', paddingBottom: 8 }} />
                        <Bar dataKey="Sent" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Delivered" fill={COLORS.info} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Opened" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Responded" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : <div />}

              {/* Cost by Period */}
              {costByPeriod.length > 0 ? (
                <div className="wa-card" style={{ marginBottom: 0 }}>
                  <div className="wa-card-head">
                    <h3 className="wa-card-title"><DollarSign size={16} /> Cost by Period</h3>
                  </div>
                  <div className="wa-card-body">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={costByPeriod} barSize={28}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={46} tickFormatter={v => `₹${v}`} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="Cost" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : <div />}

            </div>
          )}

          {/* Detailed Period Table */}
          <div className="wa-card">
            <div className="wa-card-head">
              <h3 className="wa-card-title"><Clock size={16} /> Period Details</h3>
            </div>
            <div className="wa-card-body wa-no-pad">
              <BoneSkeleton name="analytics-period-table" loading={loading} fallback={<Skeleton rows={6} />}>
                {periodAnalytics.length === 0 ? (
                  <div className="wa-empty"><Clock size={28} /><p>No period data</p></div>
                ) : (
                  <div className="wa-table-wrap">
                  <table className="wa-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Sent</th>
                        <th>Delivered</th>
                        <th>Opened</th>
                        <th>Responded</th>
                        <th>Cost</th>
                        <th>Delivery %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodAnalytics.map((p, i) => {
                        const delRate = p.sent > 0 ? ((p.delivered / p.sent) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={p.periodKey || i}>
                            <td><strong>{p.period}</strong></td>
                            <td>{fmtNum(p.sent)}</td>
                            <td style={{ color: COLORS.success, fontWeight: 600 }}>{fmtNum(p.delivered)}</td>
                            <td style={{ color: COLORS.info, fontWeight: 600 }}>{fmtNum(p.opened)}</td>
                            <td style={{ color: COLORS.purple, fontWeight: 600 }}>{fmtNum(p.responded)}</td>
                            <td>₹{p.cost.toFixed(2)}</td>
                            <td>
                              <div className="wa-rate-bar">
                                <div className="wa-rate-fill" style={{ width: `${Math.min(parseFloat(delRate), 100)}%` }} />
                                <span className="wa-rate-text">{delRate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              </BoneSkeleton>
            </div>
          </div>
        </>
      )}

      {/* ═══ TEMPLATES TAB ═══ */}
      {activeTab === 'templates' && (
        <div className="wa-grid-2" style={{ marginBottom: 16 }}>
          {/* Left: Template Bar Chart */}
          <div className="wa-card" style={{ marginBottom: 0 }}>
            <div className="wa-card-head">
              <h3 className="wa-card-title"><LayoutTemplate size={16} /> Template Performance</h3>
            </div>
            <div className="wa-card-body">
              <BoneSkeleton name="analytics-template-chart" loading={loading} fallback={<Skeleton rows={5} />}>
                {templateBarData.length === 0 ? (
                  <div className="wa-empty"><LayoutTemplate size={28} /><p>No template data</p></div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={templateBarData} barSize={14}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend verticalAlign="top" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingBottom: 8 }} />
                      <Bar dataKey="Messages" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Delivered" fill={COLORS.info} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Seen" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </BoneSkeleton>
            </div>
          </div>

          {/* Right: Template Detail Table */}
          <div className="wa-card" style={{ marginBottom: 0 }}>
            <div className="wa-card-head">
              <h3 className="wa-card-title">
                <LayoutTemplate size={16} /> Template Details
                {templatePerformance.length > 0 && (
                  <span className="wa-badge" style={{ marginLeft: 8, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600 }}>
                    {templatePerformance.length}
                  </span>
                )}
              </h3>
            </div>
            <div className="wa-card-body wa-no-pad">
              <BoneSkeleton name="analytics-template-table" loading={loading} fallback={<Skeleton rows={5} />}>
                {templatePerformance.length === 0 ? (
                  <div className="wa-empty"><LayoutTemplate size={28} /><p>No template data</p></div>
                ) : (
                  <>
                    <div className="wa-table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
                      <table className="wa-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Template</th>
                            <th>Category</th>
                            <th>Messages</th>
                            <th>Delivered</th>
                            <th>Seen</th>
                            <th>Delivery Rate</th>
                            <th>Response Rate</th>
                            <th>Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {templatePg.sliced.map((t, i) => (
                            <tr key={t.templateid || i}>
                              <td><span className="wa-rank">{(templatePg.page - 1) * 5 + i + 1}</span></td>
                              <td className="wa-cell-name" style={{ textTransform: 'capitalize' }}>{(t.templatename || '').replace(/_/g, ' ')}</td>
                              <td>
                                <span className={`wa-cat-pill ${t.templatecategory?.toLowerCase() === 'marketing' ? 'wa-cat-marketing' : 'wa-cat-utility'}`}>
                                  {t.templatecategory?.toUpperCase() || '—'}
                                </span>
                              </td>
                              <td>{fmtNum(parseInt(t.no_of_msg || 0))}</td>
                              <td style={{ color: COLORS.success, fontWeight: 600 }}>{fmtNum(parseInt(t.no_of_msg_deliverd || 0))}</td>
                              <td style={{ color: COLORS.info, fontWeight: 600 }}>{fmtNum(parseInt(t.no_of_msg_seen || 0))}</td>
                              <td>
                                <div className="wa-rate-bar">
                                  <div className="wa-rate-fill" style={{ width: `${Math.min(parseFloat(t.delivery_rate || 0), 100)}%` }} />
                                  <span className="wa-rate-text">{fmtPct(t.delivery_rate)}</span>
                                </div>
                              </td>
                              <td>
                                <span className={`wa-status-pill ${parseFloat(t.response_rate) > 80 ? 'wa-st-high' : parseFloat(t.response_rate) > 50 ? 'wa-st-mid' : 'wa-st-low'}`}>
                                  {fmtPct(t.response_rate)}
                                </span>
                              </td>
                              <td>₹{parseFloat(t.cost || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <PaginationBar {...templatePg} label="templates" />
                  </>
                )}
              </BoneSkeleton>
            </div>
          </div>
        </div>
      )}
    </div>

      {/* Campaign Analytics Modal */}
      {campaignModalOpen && (
        <CampaignAnalyticsModal
          data={campaignAnalyticsData}
          loading={campaignAnalyticsLoading}
          onClose={() => setCampaignModalOpen(false)}
        />
      )}

      {/* Message Analytics Modal */}
      {msgModalOpen && (
        <MessageAnalyticsModal
          data={msgAnalyticsData}
          loading={msgAnalyticsLoading}
          onClose={() => setMsgModalOpen(false)}
        />
      )}

      {/* Cost Analytics Modal */}
      {costModalOpen && (
        <CostAnalyticsModal
          data={costAnalyticsData}
          loading={costAnalyticsLoading}
          onClose={() => setCostModalOpen(false)}
        />
      )}

      {/* Template Analytics Modal */}
      {tplModalOpen && (
        <TemplateAnalyticsModal
          data={tplAnalyticsData}
          loading={tplAnalyticsLoading}
          onClose={() => setTplModalOpen(false)}
        />
      )}
    </>
  );
};

export default AnalyticsPage;